import { useEffect, useRef, useState } from 'react';
import {
  TerminalSquare, Folder, Clipboard, Settings, MessageSquarePlus,
  Moon, Sun, X, Grip, RefreshCcw, Upload, Search,
} from 'lucide-react';
import { useStore } from '../../store/useStore';

interface Command {
  id: string;
  label: string;
  shortcut: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string;
}

export function CommandPalette() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const close = useStore((s) => s.toggleCommandPalette);
  const openApp = useStore((s) => s.openApp);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const closeAll = useStore((s) => s.closeAllWindows);
  const theme = useStore((s) => s.theme);
  const currentProject = useStore((s) => s.currentProject);
  const syncProject = useStore((s) => s.syncProject);
  const openProject = useStore((s) => s.openProject);

  const commands: Command[] = [
    {
      id: 'terminal',
      label: 'Open Terminal',
      shortcut: 'Ctrl+T',
      icon: <TerminalSquare className="w-4 h-4" />,
      action: () => openApp('terminal'),
      keywords: 'terminal shell bash command prompt',
    },
    {
      id: 'files',
      label: 'Open Files',
      shortcut: 'Ctrl+F',
      icon: <Folder className="w-4 h-4" />,
      action: () => openApp('file-manager'),
      keywords: 'files folder drive documents manager',
    },
    {
      id: 'editor',
      label: 'Open Code Editor',
      shortcut: 'Ctrl+E',
      icon: <Clipboard className="w-4 h-4" />,
      action: () => openApp('editor'),
      keywords: 'editor code source edit monaco',
    },
    {
      id: 'projects',
      label: 'Open Projects',
      shortcut: '',
      icon: <Grip className="w-4 h-4" />,
      action: () => openApp('projects'),
      keywords: 'projects project workspace manage',
    },
    {
      id: 'chat',
      label: 'Open AI Assistant',
      shortcut: '',
      icon: <MessageSquarePlus className="w-4 h-4" />,
      action: () => openApp('chat'),
      keywords: 'ai assistant chat code help copilot',
    },
    {
      id: 'settings',
      label: 'Open Settings',
      shortcut: '',
      icon: <Settings className="w-4 h-4" />,
      action: () => openApp('settings'),
      keywords: 'settings preferences config theme',
    },
    {
      id: 'theme',
      label: `Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`,
      shortcut: 'Ctrl+Alt+T',
      icon: theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />,
      action: () => toggleTheme(),
      keywords: 'theme mode dark light switch',
    },
    {
      id: 'closeAll',
      label: 'Close All Windows',
      shortcut: '',
      icon: <X className="w-4 h-4" />,
      action: () => closeAll(),
      keywords: 'close windows minimize all',
    },
    {
      id: 'sync-down',
      label: 'Sync Project from Drive',
      shortcut: '',
      icon: <RefreshCcw className="w-4 h-4" />,
      action: () => { if (currentProject) syncProject(currentProject.id, 'down'); },
      keywords: 'sync download drive google',
      // disabled when no project
    } as Command & { disabled?: boolean },
    {
      id: 'sync-up',
      label: 'Sync Project to Drive',
      shortcut: '',
      icon: <Upload className="w-4 h-4" />,
      action: () => { if (currentProject) syncProject(currentProject.id, 'up'); },
      keywords: 'sync upload drive google push save',
    },
  ];

  const filtered = commands.filter((c) => {
    if (!query) return true;
    return (c.label + ' ' + c.keywords).toLowerCase().includes(query.toLowerCase());
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelected(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[selected];
      if (cmd && !(cmd as any).disabled) {
        cmd.action();
        close();
      }
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[20vh]"
      onClick={close}>
      <div className="animate-window-open w-full max-w-xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="glass rounded-2xl overflow-hidden shadow-2xl">
          <div className="relative px-4 py-3 border-b border-desktop-border">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-desktop-text/40" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a command or search..."
              className="w-full pl-8 pr-4 bg-transparent text-desktop-text placeholder:text-desktop-text/40 focus:outline-none text-sm"
            />
          </div>
          <div className="max-h-80 overflow-y-auto py-2">
            {filtered.length === 0 && (
              <p className="px-4 py-3 text-sm text-desktop-text/50">No commands found</p>
            )}
            {filtered.map((cmd, i) => (
              <button
                key={cmd.id}
                onClick={() => {
                  if (!(cmd as any).disabled) {
                    cmd.action();
                    close();
                  }
                }}
                onMouseEnter={() => setSelected(i)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                  i === selected ? 'bg-desktop-accent/15 text-desktop-accent' : 'text-desktop-text'
                } ${(cmd as any).disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {cmd.icon}
                  <span>{cmd.label}</span>
                </div>
                {cmd.shortcut && (
                  <kbd className="text-[10px] bg-desktop-surface2 px-1.5 py-0.5 rounded border border-desktop-border text-desktop-text/60">
                    {cmd.shortcut}
                  </kbd>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}